import { useClerk } from "@clerk/nextjs";
import { type UseMutationOptions, type UseMutationResult, useMutation } from "@tanstack/react-query";

import { AuthContext } from "@/data/auth/auth.context";
import type { ApplicationException, GeneralErrorCodes } from "@/util/vendor/error-handling";

/**
 * A wrapper around useMutation that provides auth context awareness.
 * While mutations don't have an 'enabled' property like queries, this hook
 * provides consistent auth state checking and can be extended for additional
 * auth-related mutation logic.
 *
 * @param options - Standard TanStack Query mutation options
 * @returns UseMutationResult with auth context awareness
 */
export function useAuthGuardedMutation<
  TData = unknown,
  TError = ApplicationException<GeneralErrorCodes>,
  TVariables = void,
  TContext = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
): UseMutationResult<TData, TError, TVariables, TContext> {
  const { isLoggedIn, isInitializing, isClerkTokenApplied } = AuthContext.useAuth();
  const { session } = useClerk();

  // Wrap the mutation function to check auth state before execution
  const authGuardedMutationFn = (variables: TVariables) => {
    if (isInitializing || !isLoggedIn || !session || !isClerkTokenApplied) {
      throw new Error("Authentication required: Cannot execute mutation without valid auth token");
    }
    // @ts-expect-error: Context is missing
    return options.mutationFn!(variables);
  };

  return useMutation({
    ...options,
    mutationFn: authGuardedMutationFn,
  });
}
